/**
 * Theme preference: 'system' | 'light' | 'dark'. The resolved theme lives on <html data-theme>
 * and is applied before first paint by an inline script in index.html that reads the same
 * localStorage key. The server copy (users.theme) is the source of truth across devices; on
 * sign-in it is applied and mirrored to localStorage.
 */
import { api } from './api.js';

export type ThemePref = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

const KEY = 'siva.theme';
const media = () => window.matchMedia('(prefers-color-scheme: dark)');

export function readPref(): ThemePref {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}

export function resolve(pref: ThemePref): ResolvedTheme {
  if (pref === 'system') return media().matches ? 'dark' : 'light';
  return pref;
}

export function currentTheme(): ResolvedTheme {
  return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
}

/** Set <html data-theme> (and color-scheme / theme-color) for a preference. */
export function applyTheme(pref: ThemePref, opts: { persistLocal?: boolean } = {}) {
  const resolved = resolve(pref);
  const root = document.documentElement;
  root.setAttribute('data-theme', resolved);
  root.style.colorScheme = resolved;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', resolved === 'dark' ? '#0b0d12' : '#f6f7fb');
  if (opts.persistLocal) {
    try {
      localStorage.setItem(KEY, pref);
    } catch {
      /* private mode */
    }
  }
  window.dispatchEvent(new CustomEvent('siva:theme', { detail: { pref, resolved } }));
}

/** Follow system changes while the preference is 'system'. Call once at startup. */
export function initTheme() {
  applyTheme(readPref());
  media().addEventListener('change', () => {
    if (readPref() === 'system') applyTheme('system');
  });
}

/** User toggled: store locally, apply, and persist on the server (best effort). */
export async function setThemePref(pref: ThemePref) {
  applyTheme(pref, { persistLocal: true });
  try {
    await api('/auth/theme', { method: 'PUT', body: JSON.stringify({ theme: pref }) });
  } catch {
    /* signed out or offline: the local choice still applies on this device */
  }
}

/** Cycle used by the header toggle: light → dark → light (system stays available via storage reset). */
export function nextTheme(): ThemePref {
  return currentTheme() === 'dark' ? 'light' : 'dark';
}
